import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Chip,
  Button,
  Alert,
  CircularProgress,
  InputBase,
  IconButton,
} from "@mui/material";
import { ArrowBack, TableRestaurant, RestaurantMenu, RoomService, PlaylistAdd } from "@mui/icons-material";
import { useSession } from "../context/SessionContext";
import { useOrder } from "../context/OrderContext";
import { formatPrice, orderTotal, kotPrintLines } from "../utils/helpers";
import OrderLineCard from "../components/OrderLineCard";
import GoogleReviewCard from "../components/GoogleReviewCard";
import ProductCard from "../components/ProductCard";
import ProductDetailDialog from "../components/ProductDetailDialog";
import KotConfirmDialog from "../components/KotConfirmDialog";
import AutoLoadMore from "../components/AutoLoadMore";
import useMenu from "../hooks/useMenu";
import useDebouncedValue from "../hooks/useDebouncedValue";
import { NAV_HEIGHT } from "../components/MainLayout";

const STATUS_LABEL = {
  placed: "Placed",
  // dine_in (this app's only order type) skips 'placed' entirely — see
  // LIVE_ORDER_STATUSES's comment — but reads the same to a guest.
  processing: "Placed",
  modifying: "Editing",
  dispatched: "Bill Printed",
};
const STATUS_COLOR = { placed: "primary", processing: "primary", modifying: "warning", dispatched: "info" };

// Only one live order exists for this table at a time (see OrderContext), so
// there's no list to pick from — this screen shows it directly, the moment
// there is one. Already-KOT-printed lines (kitchen has them) are always
// read-only regardless of edit state — see OrderLineCard/isKotLocked — no
// delete or quantity-reduction control for those, matching the requirement
// that this app doesn't need the staff waiter app's cancel-reason flow.
export default function LiveOrder() {
  const navigate = useNavigate();
  const { currency, tableKey, table, store } = useSession();
  const { order, isLive, isCart, lastOrder, loading, error, refresh, beginEdit, cancelEdit, submitOrder, updateItemQuantity, removeItem, submitting } =
    useOrder();

  const [busyItemId, setBusyItemId] = useState(null);
  const [actionError, setActionError] = useState("");
  const [addingItems, setAddingItems] = useState(false);
  const [kotConfirmOpen, setKotConfirmOpen] = useState(false);

  // The bill gets printed and the order gets closed at the till, and nothing
  // pushes that here — so this screen re-checks every time the guest opens it
  // and every time they come back to the tab, quietly, without blanking what
  // they're reading (see OrderContext.refresh). A cold open straight onto
  // this tab is already covered by the provider's own first load, so that one
  // isn't asked for twice.
  const coldOpen = useRef(loading);
  useEffect(() => {
    if (!coldOpen.current) refresh(true);
    coldOpen.current = false;
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isLive) {
    // An order placed in this session but no longer live: the till has closed
    // it, so the meal is over. That gets a thank-you (and the store's review
    // invite) rather than a screen that reads as if nothing ever happened -
    // unless they have since started another cart, in which case the meal
    // isn't over after all and the invite waits for that order to close.
    const served = !!lastOrder && !isCart;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "70dvh", p: 3, textAlign: "center" }}>
        {served ? (
          <RoomService sx={{ fontSize: 56, color: "primary.main", mb: 2 }} />
        ) : (
          <RestaurantMenu sx={{ fontSize: 56, color: "text.disabled", mb: 2 }} />
        )}
        <Typography variant="h6" fontWeight={700}>
          {served ? "Thanks for dining with us!" : "No live order yet"}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {served
            ? `Order #${lastOrder.order_no ?? lastOrder.id} is all settled. We hope to see you again soon!`
            : "Browse the menu and place an order to see it here."}
        </Typography>
        {served && (
          <Box sx={{ width: "100%", maxWidth: 440, textAlign: "start" }}>
            <GoogleReviewCard orderId={lastOrder.id} />
          </Box>
        )}
        <Button variant="contained" onClick={() => navigate("/")}>
          Browse Menu
        </Button>
      </Box>
    );
  }

  const items = order.order_items || [];
  const editing = order.status === "modifying";
  const dispatched = order.status === "dispatched";

  const handleQuantityChange = async (item, quantity) => {
    setBusyItemId(item.id);
    setActionError("");
    try {
      await updateItemQuantity(item, quantity);
    } catch (err) {
      setActionError(err.message || "Failed to update item");
    } finally {
      setBusyItemId(null);
    }
  };

  const handleRemove = async (item) => {
    setBusyItemId(item.id);
    setActionError("");
    try {
      await removeItem(item);
    } catch (err) {
      setActionError(err.message || "Failed to remove item");
    } finally {
      setBusyItemId(null);
    }
  };

  const handleStartEdit = async () => {
    setActionError("");
    try {
      await beginEdit();
    } catch (err) {
      setActionError(err.message || "Failed to open this order for editing");
    }
  };

  const handleCancelEdit = async () => {
    setActionError("");
    setAddingItems(false);
    try {
      await cancelEdit();
    } catch (err) {
      setActionError(err.message || "Failed to cancel changes");
    }
  };

  // Confirming here only makes sense when Save can actually trigger a KOT
  // print — the store's auto_kot_self_order setting (see OrderController@
  // checkout) — since otherwise nothing gets sent to the kitchen until staff
  // print it manually from POS/waiter, and this dialog would just be noise.
  const newKotItems = store?.settings?.auto_kot_self_order ? kotPrintLines(order) : [];

  const handleSaveClick = () => {
    setAddingItems(false);
    if (newKotItems.length > 0) {
      setKotConfirmOpen(true);
      return;
    }
    handleSave();
  };

  const handleSave = async () => {
    setActionError("");
    setKotConfirmOpen(false);
    try {
      await submitOrder();
    } catch (err) {
      setActionError(err.message || "Failed to save changes");
    }
  };

  return (
    <Box sx={{ pb: 16 }}>
      <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: "#fff", borderBottom: "1px solid", borderColor: "divider" }}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            {order.order_no ? `Order #${order.order_no}` : "Your Order"}
          </Typography>
          {tableKey && (
            <Chip icon={<TableRestaurant fontSize="small" />} label={`Table ${table?.name || tableKey}`} size="small" color="primary" variant="outlined" />
          )}
          <Chip label={STATUS_LABEL[order.status] || order.status} size="small" color={STATUS_COLOR[order.status] || "default"} />
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        {(error || actionError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || actionError}
          </Alert>
        )}
        {dispatched && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Your bill has been printed — this order is closed. Ask staff for anything further.
          </Alert>
        )}
        {/* The bill is out, so the meal is over: the store's own review
            invite, asked once and easy to wave away (GoogleReviewCard). */}
        {dispatched && <GoogleReviewCard orderId={order.id} />}

        {items.map((item) => (
          <OrderLineCard
            key={item.id}
            item={item}
            editable={editing}
            onQuantityChange={handleQuantityChange}
            onRemove={handleRemove}
            busy={busyItemId === item.id}
          />
        ))}
      </Box>

      <Box sx={{ position: "fixed", left: 0, right: 0, bottom: NAV_HEIGHT, bgcolor: "#fff", borderTop: "1px solid", borderColor: "divider", p: 2, zIndex: 15 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
          <Typography variant="h6">Total</Typography>
          <Typography variant="h6" fontWeight={800}>
            {formatPrice(orderTotal(order), currency)}
          </Typography>
        </Box>

        {dispatched ? null : editing ? (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={handleCancelEdit} disabled={submitting} sx={{ flex: "0 0 auto" }}>
              Cancel
            </Button>
            <Button variant="outlined" startIcon={<PlaylistAdd />} onClick={() => setAddingItems(true)} sx={{ flex: 1 }}>
              Add Items
            </Button>
            <Button variant="contained" disabled={submitting} onClick={handleSaveClick} sx={{ flex: 1 }}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </Box>
        ) : (
          <Button fullWidth variant="contained" size="large" onClick={handleStartEdit} sx={{ py: 1.4 }}>
            Add More Items
          </Button>
        )}
      </Box>

      {addingItems && <AddItemsOverlay onClose={() => setAddingItems(false)} />}

      <KotConfirmDialog
        open={kotConfirmOpen}
        items={newKotItems}
        onCancel={() => setKotConfirmOpen(false)}
        onConfirm={handleSave}
      />
    </Box>
  );
}

// Reuses the same menu-browsing building blocks as Home, so adding to an
// already-live order looks and behaves like the first time around — tapping
// a product opens the same full detail dialog, which adds straight against
// this order (OrderContext.addItem always targets whatever `order` currently
// is, and at this point that's the 'modifying' live order, not a fresh cart).
function AddItemsOverlay({ onClose }) {
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput);
  const { loading, loadingMore, products, hasMore, loadMoreProducts } = useMenu({ search });
  const [activeProductId, setActiveProductId] = useState(null);

  return (
    <Box sx={{ position: "fixed", inset: 0, bgcolor: "#fff", zIndex: 30, overflowY: "auto" }}>
      <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: "#fff", borderBottom: "1px solid", borderColor: "divider" }}>
        <Toolbar>
          <IconButton edge="start" onClick={onClose}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" fontWeight={700}>
            Add Items
          </Typography>
        </Toolbar>
      </AppBar>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", bgcolor: "grey.100", borderRadius: 3, px: 1.5, mb: 2 }}>
          <InputBase placeholder="Search menu" fullWidth sx={{ py: 0.8 }} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </Box>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {products.map((p) => (
              <ProductCard key={p.id} product={p} onPress={(prod) => setActiveProductId(prod.id)} />
            ))}
            <AutoLoadMore hasMore={hasMore} loadingMore={loadingMore} onLoadMore={loadMoreProducts} />
          </>
        )}
      </Box>
      <ProductDetailDialog productId={activeProductId} onClose={() => setActiveProductId(null)} />
    </Box>
  );
}
